import { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import Authentication from '../services/authentication';
import { authSessionLog } from '../services/auth-session-logger';
import { ensureSessionValidOrRefresh } from '../services/ensure-session';
import store, { AppState } from '../stores';

const authentication = new Authentication();

export type ProtectedRouteSessionStatus = 'checking' | 'allowed' | 'denied';

/**
 * 保護ルート用のセッション判定。期限切れ時は refresh を試し、
 * transient 失敗（オフライン等）では画面を維持して再試行する。
 */
export function useProtectedRouteSession(
  trigger = 'ProtectedRoute',
): ProtectedRouteSessionStatus {
  const user = useSelector((state: AppState) => state.authenticated.user);
  const accessTokenExpiresAt = useSelector(
    (state: AppState) => state.authenticated.accessTokenExpiresAt,
  );
  const lastLoggedDeniedRef = useRef(false);
  const lastLoggedTransientRef = useRef(false);

  const [status, setStatus] = useState<ProtectedRouteSessionStatus>(() => {
    if (!user) {
      return 'denied';
    }
    if (authentication.isSessionValid(accessTokenExpiresAt)) {
      return 'allowed';
    }
    return 'checking';
  });

  const attemptRefresh = useCallback(async (): Promise<void> => {
    if (!user) {
      setStatus('denied');
      return;
    }

    const latestExpiresAt = store.getState().authenticated.accessTokenExpiresAt;
    if (authentication.isSessionValid(latestExpiresAt)) {
      setStatus('allowed');
      return;
    }

    setStatus('checking');
    const result = await ensureSessionValidOrRefresh();
    if (result.ok) {
      lastLoggedDeniedRef.current = false;
      lastLoggedTransientRef.current = false;
      setStatus('allowed');
      return;
    }
    if (result.reason === 'auth') {
      if (!lastLoggedDeniedRef.current) {
        authSessionLog({
          level: 'warn',
          event: 'soft_logout',
          reason: 'auth',
          trigger,
          detail: 'protected route session refresh auth failure',
        });
        lastLoggedDeniedRef.current = true;
      }
      setStatus('denied');
      return;
    }
    if (!lastLoggedTransientRef.current) {
      authSessionLog({
        level: 'warn',
        event: 'session_kept_transient',
        reason: 'transient',
        trigger,
        detail: 'protected route kept visible after transient refresh failure',
      });
      lastLoggedTransientRef.current = true;
    }
    setStatus('allowed');
  }, [user, trigger]);

  useEffect(() => {
    if (!user) {
      setStatus('denied');
      return;
    }
    if (authentication.isSessionValid(accessTokenExpiresAt)) {
      setStatus('allowed');
      return;
    }
    void attemptRefresh();
  }, [user, accessTokenExpiresAt, attemptRefresh]);

  useEffect(() => {
    const recheck = (): void => {
      if (!user) {
        return;
      }
      const latestExpiresAt = store.getState().authenticated.accessTokenExpiresAt;
      if (authentication.isSessionValid(latestExpiresAt)) {
        setStatus('allowed');
        return;
      }
      void attemptRefresh();
    };

    window.addEventListener('online', recheck);
    const onVisibilityChange = (): void => {
      if (document.visibilityState === 'visible') {
        recheck();
      }
    };
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      window.removeEventListener('online', recheck);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [user, attemptRefresh]);

  return status;
}
